# Phase 4: Managing job ads

The retired Job Posting API had a REST-based interface for managing SEEK job ads.
The SEEK API allows you to manage SEEK job ads through GraphQL queries and mutations.
The following documentation compares examples of each type of action (create, update, expire and get) in the Job Posting API with the equivalent in the SEEK API.

## Recommendations for migration

import { Alert, Text } from 'braid-design-system';

<Alert tone="caution">
  <Text>
    Any job ads posted via the Job Posting API cannot be managed by the SEEK API
    and vice versa. We recommend a progressive rollout, moving hirers onto the
    new integration with a clear distinction of job ads managed by the Job
    Posting API versus the SEEK API.
  </Text>
</Alert>

There are two recommended ways of scaling usage of the SEEK API to all of your SEEK hirers.

### 1. Build a brand new integration

Recruitment software often has the ability to post job ads to multiple job boards at the same time for a single job requisition.
If this applies to your software, you can create an entirely new job board and integration for the SEEK API.
This also facilitates the creation of a new user experience to match up with any new value propositions within the SEEK API.

### 2. Use the SEEK API for new jobs

If you would like to maintain your current integration patterns and migrate to use the SEEK API, build your new SEEK API integration to be run in parallel with your Job Posting API integration, not for a direct changeover.
New jobs will be posted and managed via the SEEK API, while older jobs will continue to use the Job Posting API integration until they are closed.
There is currently no method of migrating jobs posted via the Job Posting API to the SEEK API.

## Data structure comparison

This table compares the data structures of a job ad in the Job Posting API with the SEEK API [`PositionProfile`] object.

Although most fields have equivalents in the SEEK API, their validation rules can diverge significantly.
For example, if a hirer isn’t configured to [link out] to an external apply form, the Job Posting API would silently ignore an `applicationFormUrl` while the SEEK API will [respond with `BAD_USER_INPUT`].
You should test your SEEK API integration to ensure it still handles invalid or conflicting inputs correctly.

import { IconTick } from 'braid-design-system';

| Job Posting API field             |  Deprecated  | [`PositionProfile`] field                                                                                                                                                                                                                          |
| :-------------------------------- | :----------: | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
| `id`                              |              | `profileId.value`                                                                                                                                                                                                                                  |
| `thirdParties.advertiserId`       |              | `positionOrganizations[].id.value`                                                                                                                                                                                                                 |
| `thirdParties.agentId`            | <IconTick /> |                                                                                                                                                                                                                                                    |
| `advertisementType`               |              | `postingInstructions[].seekAnzAdvertisementType`                                                                                                                                                                                                   |
| `jobTitle`                        |              | `positionTitle`                                                                                                                                                                                                                                    |
| `searchJobTitle`                  | <IconTick /> |                                                                                                                                                                                                                                                    |
| `location.id` + `location.areaId` |              | `positionLocation[].id.value`                                                                                                                                                                                                                      |
| `subclassificationId`             |              | `jobCategories[].id.value`                                                                                                                                                                                                                         |
| `workType`                        |              | `seekAnzWorkTypeCode`                                                                                                                                                                                                                              |
| `salary.type`                     |              | `offeredRemunerationPackage.basisCode`                                                                                                                                                                                                             |
| `salary.minimum`                  |              | `offeredRemunerationPackage.ranges[].minimumAmount.value`                                                                                                                                                                                          |
| `salary.maximum`                  |              | `offeredRemunerationPackage.ranges[].maximumAmount.value`                                                                                                                                                                                          |
| `salary.details`                  |              | `offeredRemunerationPackage.descriptions`                                                                                                                                                                                                          |
| `jobSummary`                      |              | `positionFormattedDescriptions[]` (of description `SearchSummary`)                                                                                                                                                                                 |
| `advertisementDetails`            |              | `positionFormattedDescriptions[]` (of description `AdvertisementDetails`)                                                                                                                                                                          |
| `contact`                         | <IconTick /> | Contact details will be set under `positionOpening`                                                                                                                                                                                                |
| `video.url`                       |              | `seekVideo.url`                                                                                                                                                                                                                                    |
| `video.position`                  |              | `seekVideo.position`                                                                                                                                                                                                                               |
| `applicationEmail`                | <IconTick /> | Use [Optimised Apply] to programmatically retrieve candidate applications                                                                                                                                                                          |
| `applicationFormUrl`              |              | `postingInstructions[].applicationMethods[].applicationUri.url`                                                                                                                                                                                    |
| `endApplicationUrl`               | <IconTick /> |                                                                                                                                                                                                                                                    |
| `screenId`                        | <IconTick /> | Use SEEK API [questionnaires]                                                                                                                                                                                                                      |
| `jobReference`                    |              | `seekHirerJobReference`                                                                                                                                                                                                                            |
| `agentJobReference`               |              | `seekBillingReference`                                                                                                                                                                                                                             |
| `template`                        | <IconTick /> |                                                                                                                                                                                                                                                    |
| `standout.logoId`                 | <IconTick /> | Use `postingInstructions[].brandingId`                                                                                                                                                                                                             |
| `standout.brandingId`             |              | `postingInstructions[].brandingId`                                                                                                                                                                                                                 |
| `bullets`                         |              | `positionFormattedDescriptions[]` (of description `SearchBulletPoint`)                                                                                                                                                                             |
| `recruiter.fullName`              |              | `positionOpening.postingRequester.personContacts.name.formattedName`                                                                                                                                                                               |
| `recruiter.email`                 |              | `positionOpening.postingRequester.personContacts.communication.email.address`                                                                                                                                                                      |
| `recruiter.teamName`              | <IconTick /> |                                                                                                                                                                                                                                                    |
| `additionalProperties`            | <IconTick /> | <ul><li><p>`Graduate`: Include seniority in your `positionTitle` or `positionFormattedDescriptions[]`</p></li><li><p>`ResidentsOnly`: Include requirement in your `positionFormattedDescriptions[]` or via SEEK API [questionnaires]</p></li></ul> |
| `expiryDate`                      |              | `postingInstructions[].end`                                                                                                                                                                                                                        |
| `state`                           | <IconTick /> | State is now based on `postingInstructions` start and end dates.                                                                                                                                                                                   |
| `creationId`                      |              | `postingInstructions[].idempotencyId`; see [idempotency]                                                                                                                                                                                           |

[`PositionProfile`]: /schema/named-type/PositionProfile
[Optimised Apply]: /use-cases/application-export
[idempotency]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#idempotency
[questionnaires]: /use-cases/job-posting/questionnaires
[respond with `BAD_USER_INPUT`]: /graphql/error-responses#bad_user_input
[link out]: /use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form

## Migrating endpoints to the SEEK API

The SEEK API has a number of equivalent queries & mutations for posting and managing job ads.
The following examples assume your Job Posting API integration had already [migrated to object identifiers].

| Description                                | Job Posting API endpoint                 | SEEK API operation                                                    |
| ------------------------------------------ | ---------------------------------------- | --------------------------------------------------------------------- |
| [Create an advertisement]                  | `POST` `/advertisement`                  | [`postPosition` mutation]                                             |
| [Update an advertisement]                  | `PUT` `/advertisement/<id>`              | [`updatePostedPositionProfile` mutation]                              |
| [Expire an advertisement]                  | `PATCH` `/advertisement/<id>`            | [`closePostedPositionProfile` mutation]                               |
| [Get an advertisement]                     | `GET` `/advertisement/<id>`              | [`positionProfile` query]                                             |
| [Get all advertisements]                   | `GET` `/advertisement`                   | Deprecated. Use webhooks to receive events when positions are posted. |
| [Get all advertisements for an advertiser] | `GET` `/advertisement?advertiserId=<id>` | [`positionOpenings` query]                                            |

[create an advertisement]: #create-an-advertisement
[update an advertisement]: #update-an-advertisement
[expire an advertisement]: #expire-an-advertisement
[get an advertisement]: #get-an-advertisement
[get all advertisements]: #get-all-advertisements
[get all advertisements for an advertiser]: #get-all-advertisements-for-an-advertiser
[migrated to object identifiers]: /migration-guides/job-posting-api/phase-2-object-identifiers
[`postposition` mutation]: /schema/mutation/postPosition
[`updatepostedpositionprofile` mutation]: /schema/mutation/updatePostedPositionProfile
[`closepostedpositionprofile` mutation]: /schema/mutation/closePostedPositionProfile
[`positionprofile` query]: /schema/query/positionProfile
[`positionopenings` query]: /schema/query/positionOpenings

### Create an advertisement

**Job Posting API**

`POST` to `/advertisement` with a body of the job ad contents.

```http label="Request"
POST /advertisement
Host: adposting-integration.cloud.seek.com.au

{
  "thirdParties": {
    "advertiserId": "seekAnzPublicTest:organization:seek:93WyyF1h"
  },
  "advertisementType": "Classic",
  "jobTitle": "Associate Developer",
  "location": {
    "id": "seekAnzPublicTest:location:seek:QVS62e6K"
  },
  "subclassificationId": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",
  "workType": "PartTime",
  "salary": {
    "type": "HourlyRate",
    "minimum": 70,
    "maximum": 85
  },
  "jobSummary": "Start your software development career with a dynamic & growing team",
  "advertisementDetails": "A newly created role for an ambitious <b>junior</b> or <b>graduate</b> developer. Be part of a great team in the Bendigo area.",
  "contact": {
    "name": "Mary Manager",
    "phone": "03 8517 4100",
    "email": "mary.manager@example.com"
  },
  "creationId": "6eaeb58c-cc38-4b0e-8353-daa3011f1ece"
}
```

**SEEK API**

GraphQL [`postPosition` mutation] with input of [`PostPositionInput`].

```graphql
mutation ($input: PostPositionInput!) {
  postPosition(input: $input) {
    ... on PostPositionPayload_Success {
      positionOpening {
        documentId {
          value
        }
      }
      positionProfile {
        profileId {
          value
        }
      }
    }
    ... on PostPositionPayload_Conflict {
      conflictingPositionProfile {
        profileId {
          value
        }
      }
      conflictingPositionOpening {
        documentId {
          value
        }
      }
    }
  }
}
```

```json lovato-ignore-runnable-graphql-operation
{
  "input": {
    "positionOpening": {
      "postingRequester": {
        "roleCode": "Company",
        "id": "seekAnzPublicTest:organization:seek:93WyyF1h",
        "personContacts": [
          {
            "name": {
              "formattedName": "Mary Manager"
            },
            "roleCode": "HiringManager",
            "communication": {
              "phone": [{ "formattedNumber": "03 8517 4100" }],
              "email": [
                {
                  "address": "mary.manager@example.com"
                }
              ]
            }
          }
        ]
      }
    },
    "positionProfile": {
      "positionTitle": "Associate Developer",
      "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",
      "positionFormattedDescriptions": [
        {
          "descriptionId": "SearchSummary",
          "content": "Start your software development career with a dynamic & growing team"
        },
        {
          "descriptionId": "AdvertisementDetails",
          "content": "A newly created role for an ambitious <b>junior</b> or <b>graduate</b> developer. Be part of a great team in the Bendigo area."
        }
      ],
      "offeredRemunerationPackage": {
        "basisCode": "Hourly",
        "ranges": {
          "minimumAmount": {
            "value": 70,
            "currency": "AUD"
          },
          "maximumAmount": {
            "value": 85,
            "currency": "AUD"
          },
          "intervalCode": "Hour"
        },
        "descriptions": []
      },
      "seekAnzWorkTypeCode": "PartTime",
      "jobCategories": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",
      "positionLocation": "seekAnzPublicTest:location:seek:QVS62e6K",
      "postingInstructions": {
        "seekAnzAdvertisementType": "Classic",
        "idempotencyId": "6eaeb58c-cc38-4b0e-8353-daa3011f1ece"
      }
    }
  }
}
```

For more general information, see the documentation on [posting a job ad] in the Job Posting use case.

[`postposition` mutation]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#postposition
[`postpositioninput`]: /schema/named-type/PostPositionInput
[posting a job ad]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad

### Update an advertisement

**Job Posting API**

`PUT` to `/advertisement/{advertisementId}` with a body of the job ad contents.

```http label="Request"
PUT /advertisement/8e2fde50-bc5f-4a12-9cfb-812e50500184
Host: adposting-integration.cloud.seek.com.au

{
  "thirdParties": {
    "advertiserId": "seekAnzPublicTest:organization:seek:93WyyF1h"
  },
  "advertisementType": "Premium",
  "jobTitle": "Lead Developer",
  "location": "seekAnzPublicTest:location:seek:QVS62e6K",
  "subclassificationId": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",
  "workType": "FullTime",
  "salary": {
    "type": "AnnualPackage",
    "minimum": 100000,
    "maximum": 130000,
    "details": "Up to $130k excluding super"
  },
  "jobSummary": "Experienced software developer wanted to lead an existing team.",
  "advertisementDetails": "<p>Acme Inc is looking for an experienced software developer with management experience to lead their cloud transformation.</p><p>You should bring your passion for both technology and people to this exciting role.</p>",
  "standout": {
    "bullets": [
      "Line management required",
      "Need experience with cloud deployments",
      "Competitive benefits packages"
    ]
  },
  "video": {
    "url": "https://www.youtube.com/watch?v=Q3UvigPHCjI",
    "position": "Above"
  }
}
```

**SEEK API**

GraphQL [`updatePostedPositionProfile` mutation] with input of [`UpdatePostedPositionProfileInput`].

```graphql
mutation ($input: UpdatePostedPositionProfileInput!) {
  updatePostedPositionProfile(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
```

```jsonc lovato-ignore-runnable-graphql-operation
{
  "input": {
    "positionProfile": {
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV",
      "positionTitle": "Lead Developer",
      "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",
      "positionFormattedDescriptions": [
        {
          "descriptionId": "SearchSummary",
          "content": "Experienced software developer wanted to lead an existing team."
        },
        {
          "descriptionId": "SearchBulletPoint",
          "content": "Line management required"
        },
        {
          "descriptionId": "SearchBulletPoint",
          "content": "Need experience with cloud deployments"
        },
        {
          "descriptionId": "SearchBulletPoint",
          "content": "Competitive benefits packages"
        },
        {
          "descriptionId": "AdvertisementDetails",
          "content": "<p>Acme Inc is looking for an experienced software developer with management experience to lead their cloud transformation.</p><p>You should bring your passion for both technology and people to this exciting role.</p>"
        }
      ],
      "offeredRemunerationPackage": {
        "basisCode": "Salaried",
        "ranges": {
          "minimumAmount": {
            "value": 100000,
            "currency": "AUD"
          },
          "maximumAmount": {
            "value": 130000,
            "currency": "AUD"
          },
          "intervalCode": "Year"
        },
        "descriptions": ["Up to $130k excluding super"]
      },
      "seekAnzWorkTypeCode": "FullTime",
      "seekVideo": {
        "url": "https://www.youtube.com/watch?v=Q3UvigPHCjI",
        "seekAnzPositionCode": "Above"
      },
      "jobCategories": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",
      "positionLocation": "seekAnzPublicTest:location:seek:QVS62e6K",
      "postingInstructions": {
        "seekAnzAdvertisementType": "Premium"
        // `end` date is omitted so the existing end date will be preserved.
      }
    }
  }
}
```

For more general information, see the documentation on [updating a job ad] in the Job Posting use case.

[`updatepostedpositionprofileinput`]: /schema/named-type/UpdatePostedPositionProfileInput
[updating a job ad]: /use-cases/job-posting/managing-job-ads/updating-a-job-ad

### Expire an advertisement

**Job Posting API**

`PATCH` to `/advertisement/{advertisementId}` with a body describing the field to modify.

```http label="Request"
PATCH /advertisement/8e2fde50-bc5f-4a12-9cfb-812e50500184
Host: adposting-integration.cloud.seek.com.au

[
  {
    "op": "replace",
    "path": "state",
    "value": "Expired"
  }
]
```

**SEEK API**

The SEEK API supports automatically closing job ads on the `end` date specified in their posting instructions.
This can be provided when [posting](#create-an-advertisement) or [updating](#update-an-advertisement) a job ad.
SEEK strongly recommends using automatic closure instead of scheduled events within your software.

While the [`updatePostedPositionProfile` mutation] mostly behaves like an HTTP PUT,
omitting the [`UpdatePostingInstructionInput.end`] field will preserve the existing end date like an HTTP PATCH.
See the documentation on [updating a job ad] in the Job Posting use case for more information.

You can explicitly close job ads earlier using the GraphQL [`closePostedPositionProfile` mutation] with input of [`ClosePostedPositionProfileInput`].

```graphql
mutation ($input: ClosePostedPositionProfileInput!) {
  closePostedPositionProfile(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "positionProfile": {
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
    }
  }
}
```

For more general information, see the documentation on [closing a job ad] in the Job Posting use case.

[`closepostedpositionprofile` mutation]: /use-cases/job-posting/managing-job-ads/closing-a-job-ad
[`closepostedpositionprofileinput`]: /schema/named-type/ClosePostedPositionProfileInput
[`updatepostedpositionprofile` mutation]: /schema/mutation/updatePostedPositionProfile
[`updatepostinginstructioninput.end`]: /schema/named-type/UpdatePostingInstructionInput#/field/end
[closing a job ad]: /use-cases/job-posting/managing-job-ads/closing-a-job-ad
[updating a job ad]: /use-cases/job-posting/managing-job-ads/updating-a-job-ad

### Get an advertisement

**Job Posting API**

`GET` to `/advertisement/{advertisementId}`.

```http label="Request"
GET /advertisement/1c855f8d-86a9-44f4-b7ab-250e01ff8a77
Host: adposting-integration.cloud.seek.com.au
```

```jsonc label="Response"
{
  // The SEEK API uses the object identifier in `profileId.value` to uniquely
  // identify job ads. However, these are distinct from the Job Posting API's
  // advertisement IDs.
  "id": "1c855f8d-86a9-44f4-b7ab-250e01ff8a77",

  "thirdParties": {
    // => `positionOrganizations[].id.seekAnzAdvertiserId`
    "advertiserId": "10000"
  },

  // => `positionOpening.postingRequester.personContacts[]`
  "recruiter": {
    // => `positionOpening.postingRequester.personContacts[].name.formattedName`
    "fullName": "Lucy Flatley",

    // => `positionOpening.postingRequester.personContacts[].communication.email[].address`
    "email": "Lucy_Flatley15@example.com"
  },

  // => `positionTitle`
  "jobTitle": "Chief Assurance Analyst",

  // => `positionLocation`
  "location": {
    "id": "Sydney",
    "areaId": "CBDInnerWestEasternSuburbs"
  },

  // => `jobCategories[]`
  // jobCategories requires an object identifier to specify a job category hierarchy.
  "subclassificationId": "TestingQualityAssurance",

  // => `seekAnzWorkTypeCode`
  "workType": "PartTime",

  // => `offeredRemunerationPackage`
  "salary": {
    // => `offeredRemunerationPackage.basisCode`
    "type": "HourlyRate",

    // => `offeredRemunerationPackage.ranges[].minimumAmount.value`
    "minimum": 75,

    // => `offeredRemunerationPackage.ranges[].maximumAmount.value`
    "maximum": 99.99,

    // => `offeredRemunerationPackage.descriptions`
    "details": "$75 - $100 per hour"
  },

  // => `positionFormattedDescriptions[]`
  // Formatted description should have a descriptionId.value of "SearchSummary"
  "jobSummary": "Excellent opportunity for an assurance analyst in Australia",

  // => `positionFormattedDescriptions[]`
  // Formatted description should have a descriptionId.value of "AdvertisementDetails"
  "advertisementDetails": "\n<strong>About the role</strong>\n<p>Ut necessitatibus amet deserunt laudantium veritatis. Sapiente occaecati\nodio quae quod magnam autem temporibus error rerum. Id porro distinctio eaque\neos earum sed incidunt fugiat. Minus quis iusto enim quod a. Autem sequi\nreiciendis quibusdam et nostrum.</p>\n<strong>Benefits and perks</strong>\n<p>Possimus libero sit vitae et sed quidem. Eos eveniet quo est id iste sint.\nNihil corporis in eos consequatur quas ut harum voluptates et. Deleniti minus\nvoluptatem maiores consequatur est molestiae quo est cupiditate. Mollitia\ndoloremque quam non. Omnis eum nihil nobis illo cum.</p>\n<strong>Skills and experience</strong>\n<p>Dolor quia beatae nulla perferendis culpa ea saepe ut. Excepturi non vitae\nullam consequuntur accusantium sit iste asperiores. Porro quisquam dolor\nquia. Natus ut sunt animi. Voluptatem minima perspiciatis maiores ut. Atque\nad qui sed beatae odit non.</p>\n",

  // => `seekHirerJobReference`
  "jobReference": "REQ-65558",

  // Branding and search bullets are no longer tied to `standout`.
  "standout": {
    // => `postingInstructions[].brandingId`
    "brandingId": "globalPublicTest:advertisementBranding:hirerBranding:37EWfWxhwVEGTT5BcjfE2L"
  },

  // => `postingInstructions[].seekAnzAdvertisementType`
  "advertisementType": "Classic",

  // => `postingInstructions[].end`
  "expiryDate": "2038-01-01T00:00:00Z",

  // Use `postingInstructions[].start` & `postingInstructions[].end`.
  // If current date is within these ranges, the job ad is still open.
  "state": "Open",

  "_links": {
    // GraphQL does not use HATEOAS
    "self": { "href": "/advertisements/1c855f8d-86a9-44f4-b7ab-250e01ff8a77" },

    // => `positionUri`
    "id": {
      "href": "/advertisements/1c855f8d-86a9-44f4-b7ab-250e01ff8a77/view"
    }
  }
}
```

**SEEK API**

GraphQL [`positionProfile` query] with a required `id` input.

```graphql lovato-ignore-no-deprecated-schema
query ($id: String!) {
  positionProfile(id: $id) {
    profileId {
      value
    }
    positionOrganizations {
      id {
        value
      }
      name
      seekAnzAdvertiserId
    }
    positionOpening {
      postingRequester {
        personContacts {
          name {
            formattedName
          }
          communication {
            email {
              address
            }
          }
        }
      }
    }
    positionTitle
    positionLocation {
      id {
        value
      }
      name
    }
    jobCategories {
      id {
        value
      }
      name
    }
    positionFormattedDescriptions {
      descriptionId {
        value
      }
      content
    }
    seekHirerJobReference
    seekAnzWorkTypeCode
    offeredRemunerationPackage {
      basisCode
      ranges {
        minimumAmount {
          value
          currency
        }
        maximumAmount {
          value
          currency
        }
        intervalCode
      }
      descriptions
    }
    seekVideo {
      url
      seekAnzPositionCode
    }
    postingInstructions {
      seekAdvertisementProduct {
        label
      }
      applicationMethods {
        applicationUri {
          url
        }
      }
      brandingId
      start
      end
    }
    positionUri
  }
}
```

```json label="Variables"
{
  "id": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
}
```

```json label="Response"
{
  "positionProfile": {
    "profileId": {
      "value": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
    },
    "positionOrganizations": [
      {
        "id": {
          "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
        },
        "name": "Acme Corp",
        "seekAnzAdvertiserId": 10000
      }
    ],
    "positionOpening": {
      "postingRequester": {
        "personContacts": [
          {
            "name": {
              "formattedName": "Lucy Flatley"
            },
            "communication": {
              "email": [
                {
                  "address": "Lucy_Flatley15@example.com"
                }
              ]
            }
          }
        ]
      }
    },
    "positionTitle": "Chief Assurance Analyst",
    "positionLocation": [
      {
        "id": {
          "value": "seekAnzPublicTest:location:seek:2vfkAqUFR"
        },
        "name": "Sydney Olympic Park"
      }
    ],
    "jobCategories": [
      {
        "id": {
          "value": "seekAnzPublicTest:jobCategory:seek:2MEEKv9K5"
        },
        "name": "Testing & Quality Assurance"
      }
    ],
    "positionFormattedDescriptions": [
      {
        "descriptionId": {
          "value": "SearchSummary"
        },
        "content": "Excellent opportunity for an assurance analyst in Australia"
      },
      {
        "descriptionId": {
          "value": "AdvertisementDetails"
        },
        "content": "\n<strong>About the role</strong>\n<p>Ut necessitatibus amet deserunt laudantium veritatis. Sapiente occaecati\nodio quae quod magnam autem temporibus error rerum. Id porro distinctio eaque\neos earum sed incidunt fugiat. Minus quis iusto enim quod a. Autem sequi\nreiciendis quibusdam et nostrum.</p>\n<strong>Benefits and perks</strong>\n<p>Possimus libero sit vitae et sed quidem. Eos eveniet quo est id iste sint.\nNihil corporis in eos consequatur quas ut harum voluptates et. Deleniti minus\nvoluptatem maiores consequatur est molestiae quo est cupiditate. Mollitia\ndoloremque quam non. Omnis eum nihil nobis illo cum.</p>\n<strong>Skills and experience</strong>\n<p>Dolor quia beatae nulla perferendis culpa ea saepe ut. Excepturi non vitae\nullam consequuntur accusantium sit iste asperiores. Porro quisquam dolor\nquia. Natus ut sunt animi. Voluptatem minima perspiciatis maiores ut. Atque\nad qui sed beatae odit non.</p>\n"
      }
    ],
    "seekHirerJobReference": "REQ-65558",
    "seekAnzWorkTypeCode": "PartTime",
    "offeredRemunerationPackage": {
      "basisCode": "Hourly",
      "ranges": [
        {
          "minimumAmount": {
            "value": 75,
            "currency": "AUD"
          },
          "maximumAmount": {
            "value": 99.99,
            "currency": "AUD"
          },
          "intervalCode": "Hour"
        }
      ],
      "descriptions": ["$75 - $100 per hour"]
    },
    "seekVideo": null,
    "postingInstructions": [
      {
        "seekAdvertisementProduct": {
          "label": "Classic"
        },
        "applicationMethods": [],
        "brandingId": "globalPublicTest:advertisementBranding:hirerBranding:37EWfWxhwVEGTT5BcjfE2L",
        "start": "2019-08-20T21:02:27.101Z",
        "end": "2038-01-01T00:00:00.000Z"
      }
    ],
    "positionUri": "https://au.candidate.example.seek.com/job/30000000"
  }
}
```

For more general information, see the documentation on [querying a job ad] in the Job Posting use case.

[`positionprofile` query]: /schema/query/positionProfile
[querying a job ad]: /use-cases/job-posting/managing-job-ads/querying-a-job-ad

### Get all advertisements

**Job Posting API**

`GET` to `/advertisement`.

```http label="Request"
GET /advertisement
Host: adposting-integration.cloud.seek.com.au
```

**SEEK API**

It’s not possible to enumerate all advertisements for an integration partner in the SEEK API.
You can only get all advertisements for a particular hirer using [`positionOpenings` query].

You can also be notified when a job ad goes live with the [`PositionProfilePosted` event], which is detailed in the next phase.

[`positionprofileposted` event]: /schema/named-type/PositionProfilePostedEvent

### Get all advertisements for an advertiser

**Job Posting API**

`GET` to `/advertisement?advertiserId={advertiserID}`.

```http label="Request"
GET /advertisement?advertiserId=10000
Host: adposting-integration.cloud.seek.com.au
```

**SEEK API**

GraphQL [`positionOpenings` query] with a required `hirerId` input.
The `positionOpenings` query returns a paginated list of [`PositionOpening`] objects.
You can select fields from its nested [`PositionOpening.paginatedPositionProfiles`] to retrieve information about a subset of its job ads.

```graphql
query ($hirerId: String!) {
  positionOpenings(hirerId: $hirerId) {
    edges {
      node {
        statusCode
        paginatedPositionProfiles {
          edges {
            node {
              profileId {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
```

```json
{
  "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
}
```

For more general information, see the documentation on [position openings] in the Job Posting use case.

[`positionopenings` query]: /schema/query/positionOpenings
[`positionopening`]: /schema/named-type/PositionOpening
[`positionprofile`]: /schema/named-type/PositionProfile
[`positionopening.paginatedpositionprofiles`]: /schema/named-type/PositionOpening#/field/paginatedPositionProfiles
[position openings]: /use-cases/job-posting/position-openings

## Exit criteria

To move to the next phase, complete the following:

| Criteria                        | Description                                                      |
| ------------------------------- | ---------------------------------------------------------------- |
| Existing integration maintained | Hirer can continue to manage jobs posted via the Job Posting API |
| Job ad posted successfully      | Hirer can successfully post a job ad via the SEEK API            |
| Job ad updated successfully     | Hirer can successfully update a job ad via the SEEK API          |
| Job ad closed successfully      | Hirer can successfully close a job ad via the SEEK API           |
| Job ad viewed successfully      | Hirer can view job ad details via the SEEK API                   |
| Job ads listed successfully     | Hirer can see a list of their job ads posted via the SEEK API    |
